import { type JSX } from "react";
import { Navigate } from "react-router";
import { PERMISSIONS } from "@/shared/consts";
import { runtimeConfig } from "@/shared/config/runtime";
import { useAppSelector, useAuth } from "@/shared/hooks";
import { LOGIN_PAGE_ROUTE, DEMO_CREATE_ROUTE } from "@/shared/utils";
import { AccessDeniedPage } from "@/shared/components";

interface PermissionProtectedRouteProps {
  children: JSX.Element;
  requiredPermissions?: string[];
  requiredRoles?: string[];
  requiredFeature?: string;
  alwaysAccessible?: boolean;
}

export default function PermissionProtectedRoute({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  requiredFeature,
  alwaysAccessible,
}: PermissionProtectedRouteProps) {
  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const { user, hasAnyPermission, hasAnyRole, hasFeature } = useAuth();

  if (!isAuth) {
    return <Navigate to={runtimeConfig.isDemo ? DEMO_CREATE_ROUTE : LOGIN_PAGE_ROUTE} replace />;
  }

  if (alwaysAccessible) {
    return children;
  }

  if (requiredFeature && !hasFeature(requiredFeature)) {
    return <AccessDeniedPage />;
  }

  if (requiredRoles.length > 0) {
    if (!hasAnyRole(requiredRoles)) {
      return <AccessDeniedPage />;
    }
  }

  if (requiredPermissions.length > 0) {
    if (!hasAnyPermission(requiredPermissions)) {
      return <AccessDeniedPage />;
    }
  }

  if (requiredRoles.length === 0 && requiredPermissions.length === 0) {
    if (user?.user_type !== "expert" && !hasAnyPermission([PERMISSIONS.SELF.VIEW_PROFILE])) {
      return <AccessDeniedPage />;
    }
  }

  return children;
}

